import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Wallet, DollarSign, Save, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import AdminLayout from '../components/AdminLayout';
import { getSettings, updateSetting } from '../services/api';
import { toast } from 'sonner';

const AdminSettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await getSettings();
      const settingsObj = {};
      response.data.forEach(s => {
        settingsObj[s.key] = s.value;
      });
      setSettings(settingsObj);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (key) => {
    setSaving({ ...saving, [key]: true });
    try {
      await updateSetting({ key, value: settings[key] });
      toast.success('Setting updated');
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSaving({ ...saving, [key]: false });
    }
  };

  const handleChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const walletSettings = [
    { key: 'btc_wallet', label: 'Bitcoin (BTC) Wallet Address', icon: '₿', color: 'from-orange-500 to-yellow-500' },
    { key: 'eth_wallet', label: 'Ethereum (ETH) Wallet Address', icon: 'Ξ', color: 'from-purple-500 to-blue-500' },
    { key: 'usdt_wallet', label: 'Tether (USDT) Wallet Address', icon: '₮', color: 'from-emerald-500 to-green-500' },
  ];

  const financialSettings = [
    { key: 'min_deposit', label: 'Minimum Deposit (USD)', type: 'number' },
    { key: 'max_deposit', label: 'Maximum Deposit (USD)', type: 'number' },
    { key: 'min_withdrawal', label: 'Minimum Withdrawal (USD)', type: 'number' },
    { key: 'max_withdrawal', label: 'Maximum Withdrawal (USD)', type: 'number' },
    { key: 'min_investment', label: 'Minimum Investment (USD)', type: 'number' },
    { key: 'deposit_fee', label: 'Deposit Fee (%)', type: 'number' },
    { key: 'withdrawal_fee', label: 'Withdrawal Fee (%)', type: 'number' },
    { key: 'daily_profit_percentage', label: 'Daily Profit Percentage (%)', type: 'number' },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page">
        {/* Header */}
        <div className="admin-page-header mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] text-white">
            Platform Settings
          </h1>
          <p className="text-slate-400">Configure wallet addresses, fees, and limits</p>
        </div>

        {/* Wallet Addresses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-cyan-400" />
            Cryptocurrency Wallet Addresses
          </h2>

          <div className="grid lg:grid-cols-3 gap-6">
            {walletSettings.map((wallet) => (
              <div key={wallet.key} className="glass-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${wallet.color} flex items-center justify-center`}>
                    <span className="text-white font-bold">{wallet.icon}</span>
                  </div>
                  <h3 className="text-white font-semibold">{wallet.label.split('(')[0]}</h3>
                </div>

                <div className="mb-4">
                  <textarea
                    value={settings[wallet.key] || ''}
                    onChange={(e) => handleChange(wallet.key, e.target.value)}
                    className="input-field font-mono text-sm h-24 resize-none"
                    placeholder="Enter wallet address..."
                    data-testid={`input-${wallet.key}`}
                  />
                </div>

                {settings[wallet.key] && (
                  <div className="flex justify-center mb-4">
                    <div className="bg-white p-3 rounded-xl">
                      <QRCodeSVG value={settings[wallet.key]} size={120} level="H" />
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleSave(wallet.key)}
                  disabled={saving[wallet.key]}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid={`save-${wallet.key}`}
                >
                  {saving[wallet.key] ? (
                    <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Financial Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-400" />
            Financial Settings
          </h2>

          <div className="glass-card p-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {financialSettings.map((setting) => (
                <div key={setting.key}>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {setting.label}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type={setting.type}
                      value={settings[setting.key] || ''}
                      onChange={(e) => handleChange(setting.key, e.target.value)}
                      className="input-field flex-1"
                      min="0"
                      step="0.01"
                      data-testid={`input-${setting.key}`}
                    />
                    <button
                      onClick={() => handleSave(setting.key)}
                      disabled={saving[setting.key]}
                      className="p-3 rounded-lg bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                      data-testid={`save-${setting.key}`}
                    >
                      {saving[setting.key] ? (
                        <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Payment Method Toggles */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8"
        >
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <Settings className="w-5 h-5 text-purple-400" />
            Payment Methods
          </h2>

          <div className="glass-card p-6">
            <div className="grid md:grid-cols-3 gap-6">
              {['btc', 'eth', 'usdt'].map((crypto) => (
                <div key={crypto} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <span className="text-white font-medium">{crypto.toUpperCase()} Payments</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[`${crypto}_enabled`] === 'true'}
                      onChange={(e) => {
                        handleChange(`${crypto}_enabled`, e.target.checked.toString());
                        setTimeout(() => handleSave(`${crypto}_enabled`), 100);
                      }}
                      className="sr-only peer"
                      data-testid={`toggle-${crypto}`}
                    />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-500"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
